import type { ComponentProps } from 'react';
import type { MDXComponents } from '../../lib/blog/meta';
import { Figure } from './Figure';
import { Logo } from './Logo';
import { CTA } from './CTA';
import { SummaryCallout } from './SummaryCallout';
import { YouTube } from './YouTube';
import { Tweet } from './Tweet';
import { Chart } from './Chart';
import { Price } from './Price';
import { PricedBarChart } from './PricedBarChart';
import { Pre } from './Pre';
import { CodeTabs } from './CodeTabs';
import { RelatedGuides } from './RelatedGuides';
import { FAQ } from './Faq';
import { MdxLink } from './MdxLink';
import { Heatmap } from '../charts/Heatmap';
import { DonutChart } from '../charts/DonutChart';
import { Timeline } from '../charts/Timeline';
import { SequenceDiagram } from '../charts/SequenceDiagram';
import { RoutingLadder } from '../charts/RoutingLadder';

/**
 * Wraps a markdown table in a horizontally scrollable block so a wide table
 * scrolls inside its own box instead of widening the whole page on narrow
 * viewports. Styling stays in `.blog-prose table` (app.css).
 */
const ScrollableTable = (props: ComponentProps<'table'>) => (
  <div className="overflow-x-auto">
    <table {...props} />
  </div>
);

/**
 * The component map handed to every MDX post. Authors (and the content-engine)
 * use the capitalized tags by name without importing them; `a`, `pre`, and
 * `table` override the intrinsic elements for router links, copyable code
 * blocks, and overflow-safe tables. Base prose elements (h2/p/ul/…) are styled
 * via the `.blog-prose` class in app.css.
 */
export const mdxComponents: MDXComponents = {
  a: MdxLink,
  pre: Pre,
  table: ScrollableTable,
  CodeTabs,
  RelatedGuides,
  FAQ,
  // `SummaryCallout` under the `Callout` name: posts keep writing `<Callout>`,
  // and the one that opens the post carries the product card. See that file.
  Callout: SummaryCallout,
  Figure,
  Logo,
  CTA,
  YouTube,
  Tweet,
  Chart,
  Price,
  Heatmap,
  // Priced bars: a bar in a post may name a SKU in place of a typed price. See that file.
  BarChart: PricedBarChart,
  DonutChart,
  Timeline,
  SequenceDiagram,
  RoutingLadder,
};
